import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, Output, Renderer2 } from '@angular/core';
import { isNumber, isString } from '../../models/utils';

@Directive({
    standalone: false,
    selector: '[resScrollablePopover]'
})
export class ScrollablePopoverDirective implements AfterViewInit {
    private _resScrollablePopover: boolean;
    get resScrollablePopover(): boolean {
        return this._resScrollablePopover;
    }
    @Input()
    set resScrollablePopover(val: boolean) {
        if (this._resScrollablePopover !== val) {
            this._resScrollablePopover = val;
            if (!val) {
                this.closingByClick = false;
            }
            this.resScrollablePopoverChange.emit(this.resScrollablePopover);
            this.setPopover();
        }
    }
    @Output() resScrollablePopoverChange = new EventEmitter<boolean>();
    @Output() showPrevious = new EventEmitter();
    @Output() showNext = new EventEmitter();

    private _popoverErrorMessage: string;
    get popoverErrorMessage(): string {
        return this._popoverErrorMessage;
    }
    @Input()
    set popoverErrorMessage(val: string) {
        if (this._popoverErrorMessage !== val) {
            this._popoverErrorMessage = val;
            if (this.popoverElement) {
                this.renderer.setProperty(this.textElement, 'innerHTML', this.popoverErrorMessage);
            }
        }
    }

    private _showUpDownArrows: boolean;
    get showUpDownArrows(): boolean {
        return this._showUpDownArrows;
    }
    @Input()
    set showUpDownArrows(val: boolean) {
        if (this._showUpDownArrows !== val) {
            this._showUpDownArrows = val;
            // This condition is to add the desired icons only once
            if (this.popoverElement) {
                const upArrow = this.renderer.createElement('i');
                const downArrow = this.renderer.createElement('i');
                if (this.showUpDownArrows && (!document.getElementById('fa fa-chevron-up') && !document.getElementById('fa fa-chevron-down'))) {
                    this.renderer.setAttribute(this.bodyElement, 'class', 'popover-content popover-body popover-with-chevrons');

                    this.renderer.setAttribute(upArrow, 'class', 'fa fa-chevron-up');
                    this.renderer.appendChild(this.bodyElement, upArrow);
                    upArrow.addEventListener('click', (event: MouseEvent) => {
                        this.showPrevious.emit();
                    });

                    this.renderer.setAttribute(downArrow, 'class', 'fa fa-chevron-down');
                    this.renderer.appendChild(this.bodyElement, downArrow);
                    downArrow.addEventListener('click', (event: MouseEvent) => {
                        this.showNext.emit();
                    });

                    if (this.popoverAutomationId) {
                        this.renderer.setAttribute(upArrow, 'data-automation-id', this.popoverAutomationId + '-previous-button');
                        this.renderer.setAttribute(downArrow, 'data-automation-id', this.popoverAutomationId + '-next-button');
                    }
                } else if (!val) {
                    this.renderer.removeClass(this.bodyElement, 'popover-with-chevrons');

                    const upEl = document.getElementsByClassName('fa fa-chevron-up');
                    const downEl = document.getElementsByClassName('fa fa-chevron-down');
                    upEl[0].parentNode.removeChild(upEl[0]);
                    downEl[0].parentNode.removeChild(downEl[0]);
                }
            }
        }
    }

    private _popoverType: string;
    get popoverType(): string {
        return this._popoverType;
    }
    @Input()
    set popoverType(val: string) {
        if (this._popoverType !== val) {
            const wasInfo = this._popoverType === 'info';
            if (this.popoverElement && isString(this._popoverType)) {
                this.renderer.removeClass(this.popoverElement, this._popoverType);
            }
            this._popoverType = val;
            if (this.popoverElement) {
                this.renderer.addClass(this.popoverElement, this.popoverType);
            }
            // When type changes from info to error, reset dismiss so the new error shows
            if (wasInfo && this.closingByClick) {
                this.closingByClick = false;
                this.setPopover();
            }
        }
    }

    @Input() popoverPlacement: string;
    @Input() popoverOffsetTop: number;
    @Input() popoverOffsetLeft: number;
    @Input() popoverAutomationId: string;
    @Input() isSingleLine = false;
    @Input() isLineSeparator = false;
    @Input() isPopoverArrowHidden = false;
    @Input() popoverStickyClass: string;
    closingByClick = false;

    inputElement: HTMLElement;
    private popoverElement;
    private textElement;
    private parentElement: HTMLElement;
    private bodyElement: HTMLElement

    constructor(private renderer: Renderer2, private element: ElementRef) {
        this.inputElement = this.element.nativeElement as HTMLElement;
        this.parentElement = this.renderer.parentNode(this.element.nativeElement) as HTMLElement;
    }

    ngAfterViewInit(): void {
        this.buildPopover();
    }

    buildPopover(): void {
        if (this.parentElement) {
            this.renderer.setStyle(this.parentElement, 'position', 'relative');

            this.popoverElement = this.renderer.createElement('div');
            this.renderer.setAttribute(this.popoverElement, 'class', 'scrollable-popover popover' +
                (this.isSingleLine ? ' single-line' : '') +
                (this.isLineSeparator ? ' separate-line' : '') +
                (this.popoverStickyClass ? ` ${this.popoverStickyClass}` : '') +
                (this.isPopoverArrowHidden ? ` ${this.isPopoverArrowHidden}` : '') +
                (this.popoverPlacement ? (' ' + this.popoverPlacement) : '') +
                (this.popoverPlacement ? (' bs-popover-' + this.popoverPlacement) : '') +
                (this.popoverType ? (' ' + this.popoverType) : ''));

            if (!this.isPopoverArrowHidden) {
                const arrowElement = this.renderer.createElement('div');
                this.renderer.setAttribute(arrowElement, 'class', 'popover-arrow arrow');
                this.renderer.appendChild(this.popoverElement, arrowElement);
            }

            this.bodyElement = this.renderer.createElement('div');
            this.renderer.setAttribute(this.bodyElement, 'class', 'popover-content popover-body');
            this.textElement = this.renderer.createElement('span');
            if (this.popoverErrorMessage) {
                this.renderer.setProperty(this.textElement, 'innerHTML', this.popoverErrorMessage);
            }
            if (this.popoverAutomationId) {
                this.renderer.setAttribute(this.textElement, 'data-automation-id', this.popoverAutomationId);
            }
            this.renderer.appendChild(this.bodyElement, this.textElement);

            const imageElement = this.renderer.createElement('i');
            this.renderer.setAttribute(imageElement, 'class', 'fa fa-close');
            if (this.popoverAutomationId) {
                this.renderer.setAttribute(imageElement, 'data-automation-id', this.popoverAutomationId + '-close-button');
            }
            imageElement.addEventListener('click', (event: MouseEvent) => {
                this.closingByClick = true;
                this.setPopover();
                if (event) {
                    event.stopPropagation();
                }
            });
            this.renderer.appendChild(this.bodyElement, imageElement);

            this.renderer.appendChild(this.popoverElement, this.bodyElement);

            this.renderer.appendChild(this.parentElement, this.popoverElement);

            if (!isNumber(this.popoverOffsetTop)) {
                this.popoverOffsetTop = 0;
            }
            if (!isNumber(this.popoverOffsetLeft)) {
                this.popoverOffsetLeft = 0;
            }

            this.setPopover();
        }
    }

    @HostListener('focusin')
    onFocus(): void {
        if (this.closingByClick && this._popoverType !== 'info') {
            this.closingByClick = false;
            this.setPopover();
        }
    }

    setPopover(): void {
        if (this.popoverElement) {
            const shouldShow = this.resScrollablePopover && !this.closingByClick;
            this.renderer.setStyle(this.popoverElement, 'display', shouldShow ? 'block' : 'none');
            if (shouldShow) {
                let popOffsetTop = this.popoverOffsetTop;
                let popOffsetLeft = this.popoverOffsetLeft;
                if (this.popoverPlacement === 'top') {
                    popOffsetTop += this.element.nativeElement.offsetTop - this.popoverElement.offsetHeight - 6;
                    popOffsetLeft += this.element.nativeElement.offsetLeft;
                } else if (this.popoverPlacement === 'bottom') {
                    popOffsetTop += this.element.nativeElement.offsetTop + this.element.nativeElement.offsetHeight + 1;
                    popOffsetLeft += this.element.nativeElement.offsetLeft;
                } else if (this.popoverPlacement === 'left') {
                    popOffsetTop += this.element.nativeElement.offsetTop;
                    popOffsetLeft += this.element.nativeElement.offsetLeft - this.popoverElement.offsetWidth - 6;
                } else if (this.popoverPlacement === 'right') {
                    popOffsetTop += this.element.nativeElement.offsetTop;
                    popOffsetLeft += this.element.nativeElement.offsetLeft + this.element.nativeElement.offsetWidth + 1;
                }
                this.renderer.setStyle(this.popoverElement, 'top', popOffsetTop + 'px');
                this.renderer.setStyle(this.popoverElement, 'left', popOffsetLeft + 'px');
            }
        }
    }

    resetDismiss(): void {
        this.closingByClick = false;
        this.setPopover();
    }

    focusOnInput(): void {
        let inputElement = this.element.nativeElement as HTMLElement;
        if (inputElement.tagName !== 'INPUT') {
            inputElement = (this.element.nativeElement.getElementsByTagName('INPUT').item(0) as HTMLElement) || this.element.nativeElement as HTMLElement;
        }
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        inputElement.focus();
    }
}
